<template>
  <div class="ui large modal zw transitionstyle" v-cloak>
    <div class="ui five top attached zw steps" v-cloak>
      <div class="step" v-for="(tab, index) in tabs" :key="index" @click="switchTab(index)"
           :class="{ 'active' : currentTab === index }" :id="tab.name">
        <i class="icon" :class="tab.icon"/>
        <div class="content">
          <div class="title">{{ tab.title }}</div>
          <div class="description">{{ tab.description }}</div>
        </div>
      </div>
    </div>

    <div class="ui active basic tab segment" v-if="tabs[currentTab].name === 'product' && tabExists('product')">
      <div class="content">
        <h3 class="ui header">
          {{ subscription.product.name }}
          <div class="ui sub header" v-html="subscription.product.intro"></div>
        </h3>
        <div class="ui divider"></div>
        <h4 class="ui header">{{ $t('modals.newsubscriptionmodal.product.content.description') }}</h4>
        <div class="description" v-html="subscription.product.description"></div>
      </div>
    </div>

    <div class="ui active basic tab segment" v-if="tabs[currentTab].name === 'accessories' && tabExists('accessories')">
      <div class="content">
        <div class="ui four stackable cards">
          <accessory v-for="accessory in subscription.product.accessories" :accessory="accessory"
                     :addedAccessories="selectedAccessories" :changeable="true" :key="accessory.id"
                     @selected="selectAccessory" @deselected="deselectAccessory"/>
        </div>
      </div>
    </div>

    <div class="ui active basic tab segment" v-show="tabs[currentTab].name === 'details'" v-if="tabExists('details')">
      <div class="content">
        <div class="ui header">{{ $t('modals.newsubscriptionmodal.details.content.header') }}</div>

        <div class="ui form" :data-vv-scope="formScope">
          <div class="two fields" :key="index" v-for="(fields, index) in chunkProperty(subscription.product.fields, 2)">
            <field :field="field" :allowEdit="field.editable" :scope="formScope" :transparent="false" v-if="field"
                   :value="fieldValues" @input="changeFieldValue" v-for="field in fields" :key="field.id"/>
          </div>
          <div class="field" :class="{ 'error' : errors.has(formScope + '.terms') }">
            <div class="ui checkbox" id="termsbox">
              <input name="terms" :data-vv-as="$t('modals.newsubscriptionmodal.details.terms')"
                     :checked="acceptingTerms" type="checkbox" @change="changeTermsValue()" :data-vv-scope="formScope"
                     v-validate="'required'">
              <label>{{ $t('modals.newsubscriptionmodal.details.agree_to') }} <a @click.prevent="showTermsModal = true">{{
                  $t('modals.newsubscriptionmodal.details.terms')
                }}</a></label>
              <span style="color: red;" v-if="errors.has(formScope + '.terms')">{{
                  errors.first(formScope + '.terms')
                }}</span>
            </div>
          </div>
        </div>
      </div>

      <semantic-modal v-model="showTermsModal"
                      :settings="{content_classes: {content: true}, closeable: false, closeable_button: false}">
        <div class="ui header" slot="header">
          {{ $t('modals.paymentterms.header') }}
        </div>
        <payment-terms/>
        <div class="actions" slot="actions">
          <div class="ui red button" @click="denyTerms()">{{ $t('globals.buttons.no') }}</div>
          <div class="ui ok green button" @click="acceptTerms()">{{ $t('globals.buttons.accept') }}</div>
        </div>
      </semantic-modal>
    </div>

    <div class="ui active basic tab segment" v-if="tabs[currentTab].name === 'payment' && tabExists('payment')">
      <div class="content">
        <h4 class="ui header">{{ $t('modals.newsubscriptionmodal.payment.content.header') }}</h4>
        <span v-if="subscription.charge" style="font-size: 16px; font-weight: 700;">
					<i class="blue info circle icon"/> {{ $t('modals.newsubscriptionmodal.payment.content.instant_payment') }}
				</span>
        <div class="ui divider"></div>

        <div class="ui zw three flex-center stackable cards">
          <payment-plan v-for="pricing in subscription.pricing_plans" @close="refreshPage"
                        @selectpayplan="selectPaymentplan" @deselectpayplan="deselectPaymentplan" :pricing="pricing"
                        :active="selectedPaymentplan === pricing.option_id" :key="pricing.id"
                        :subscription="subscription"/>
        </div>
      </div>
    </div>

    <div class="ui active basic tab segment"
         v-if="tabs[currentTab].name === 'payment_method' && tabExists('payment_method')">
      <div class="content">
        <h4 class="ui header">{{ $t('modals.newsubscriptionmodal.payment_method.description') }}</h4>
        <p v-if="isDefaultPaymentMethod && filteredPaymentMethods.length > 0" class="text-info text-danger">
          * {{ $t('modals.newsubscriptionmodal.payment_method.default_payment_method') }}
        </p>
        <div class="ui divider"></div>
        <div class="ui zw two flex-center stackable cards">
          <template v-if="filteredPaymentMethods.length > 0">
            <payment-method v-for="paymentMethod in filteredPaymentMethods" :key="paymentMethod.id" :id="paymentMethod.id"
                            :name="paymentMethod.name" :slug="paymentMethod.slug" @select="selectPaymentMethod"
                            @deselect="deselectPaymentMethod" :active="selectedPaymentMethod === paymentMethod.slug"
                            :editable="!isDefaultPaymentMethod" :payment-plan="selectedPaymentType"/>
          </template>
          <template v-else>
            <b class="text-center" style="font-size: 20px; margin: 20px 0;">{{ $t('globals.validation.warning.not_all_steps_completed', {0: $t('globals.container.waitlist')}) }}</b>
          </template>
        </div>
        <div class="ui divider notice"
             v-if="selectedPaymentplan && ((filteredPaymentMethods.length === 1 && selectedPaymentMethod === null) || selectedPaymentMethod === 'nets')">
          {{ $t('modals.nets_notice') }}
        </div>
        <div class="ui divider notice" v-if="selectedPaymentplan && selectedPaymentMethod === 'vipps'">
          <template v-if="vippsPaymentAllowed">
            <template v-if="selectedPaymentType === 'instant'">
              <template v-if="subscription.transaction_date > currentDate">
                {{ $t('modals.vipps_notice_later', {transaction_date: subscription.transaction_date}) }}
              </template>
              <template v-else>
                {{ $t('modals.vipps_notice') }}
              </template>
            </template>
            <template v-else>
              {{ $t('modals.vipps_notice_recurring') }}
            </template>
          </template>
          <template v-else>
            Unfortunately, Vipps payment method not allowed for this Waiting List. Please contact the Merchant.
          </template>
        </div>
      </div>
    </div>

    <div class="actions" style="margin-bottom: 1rem;">
      <div class="ui red button" @click="cancelModal()">{{ $t('globals.buttons.cancel') }}</div>
      <div class="ui primary prev button" @click="prevTab()" v-if="showPrevious">{{
          $t('globals.buttons.previous')
        }}
      </div>
      <div class="ui primary zw next button" @click="nextTab()" v-if="showNext">{{ $t('globals.buttons.next') }}</div>
      <template v-else-if="!showNext">
        <vipps-mobilepay-button
          v-if="selectedPaymentMethod === 'vipps'"
          :class="{ 'disabled' : disableActivateButton }"
          :disabled="disableActivateButton"
          @click="validateModal()"
          type="button"
          brand="vipps"
          :language="getLanguage"
          variant="primary"
          rounded="false"
          verb="pay"
          :stretched="isMobile"
          branded="true"
          :loading="buttonLoader"></vipps-mobilepay-button>
        <div class="ui button green" @click="validateModal()" v-else
             :class="[{ 'loading disabled' : buttonLoader }, { 'disabled' : disableActivateButton }]" id="subscribe">
          <span>{{ $t('globals.buttons.finish') }}</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import chunk from 'lodash/chunk'
import size from 'lodash/size'
import find from 'lodash/find'
import findIndex from 'lodash/findIndex'

export default {
  name: 'NewWaitlistModal',

  props: {
    active: {
      required: true,
      type: Boolean,
      default: false
    },

    subscription: {
      required: true
    }
  },

  data () {
    return {
      showPrevious: false,
      showNext: true,
      buttonLoader: false,
      formScope: 'detailsForm',
      currentDate: new Date().toISOString().split('T')[0],
      tabs: [
        {
          name: null,
          icon: null,
          title: null,
          description: null
        }
      ],
      currentTab: 0,
      selectedAccessories: {},
      showTermsModal: false,
      acceptingTerms: false,
      fieldValues: [],
      selectedPaymentplan: null,
      selectedPaymentPrice: null,
      selectedPaymentType: null,
      selectedPaymentMethod: null,
      isDefaultPaymentMethod: false,
      paymentMethods: [
        {
          id: 1,
          name: 'Nets',
          slug: 'nets'
        },
        {
          id: 2,
          name: 'Vipps/MobilePay',
          slug: 'vipps'
        }
      ],
      filteredPaymentMethods: [],
      vippsPaymentAllowed: true
    }
  },

  watch: {
    active: function () {
      this.selectedAccessories = this.subscription.accessories !== 'null' ? JSON.parse(this.subscription.accessories) : {}
      this.selectedPaymentplan = this.subscription.option
      this.selectedPaymentType = this.subscription.pricing ? this.subscription.pricing.type : null
      this.isDefaultPaymentMethod = this.subscription.product.payment_method !== null
      this.selectedPaymentMethod = this.subscription.payment_method
      this.acceptingTerms = false

      if (this.isDefaultPaymentMethod) {
        this.selectedPaymentMethod = this.subscription.product.payment_method
        const _this = this

        this.filteredPaymentMethods = this.paymentMethods.filter(function (currentValue) {
          if (currentValue.slug === _this.selectedPaymentMethod) {
            return currentValue
          }
        })
      }

      if (this.selectedPaymentType && !this.isDefaultPaymentMethod) {
        const _this = this

        this.filteredPaymentMethods = this.paymentMethods.filter(function (currentValue) {
          if (currentValue.slug === 'vipps') {
            if (_this.$store.getters.activeMerchant.vipps) {
              let currentDate = new Date()
              const numberOfDaysToAdd = 2
              let result = currentDate.setDate(currentDate.getDate() + numberOfDaysToAdd)
              let vippsMinimumTransactionDate = new Date(result).toISOString().split('T')[0]

              if (_this.selectedPaymentType === 'recurring' && (_this.subscription.transaction_date <= _this.currentDate || _this.subscription.transaction_date > vippsMinimumTransactionDate)) {
                return currentValue
              } else if (_this.selectedPaymentType === 'instant') {
                return currentValue
              }
            }
          } else if (currentValue.slug === 'nets') {
            return currentValue
          }
        })
      }

      this.loadTabs()
        .then(filteredTabs => {
          this.tabs = filteredTabs
          $(this.$el).modal('refresh').modal('toggle') // show modal
        })
    },

    currentTab: function (newTab) {
      $(window).trigger('resize')
      this.checkButtons()
    },

    subscription: function (newSubscription) {
      this.subscription = newSubscription
    }
  },

  mounted () {
    const _this = this
    // initialize modal
    this.$nextTick(function () {
      $(this.$el).modal({
        observeChanges: true,
        detachable: true,
        closable: false,
        closeable: false,
        transition: 'fade',

        dimmerSettings: {
          closable: false,
          closeable: false,
          useCSS: true
        },

        onVisible: function () {
          $(_this.$el).modal('refresh')
        }
      })
    })
  },

  methods: {
    changeFieldValue (field) {
      if (!find(this.fieldValues, {id: field.id})) {
        this.fieldValues.push(field)
      } else {
        var arrayValue = find(this.fieldValues, {id: field.id})
        arrayValue.value = field.value
      }
    },

    chunkProperty (chunkedProperty, value) {
      return chunk(chunkedProperty, value)
    },

    changeTermsValue () {
      this.acceptingTerms = !this.acceptingTerms
    },

    saveDetails (scope) {
      return new Promise((resolve, reject) => {
        this.$validator.validateAll(scope)
          .then(result => {
            if (result) {
              const fields = []

              $.map(this.fieldValues, function (item, index) {
                if (item.editable) {
                  if (item.required) {
                    if (item.value !== '') {
                      fields.push({id: item.id, value: item.value})
                    }
                  } else {
                    if (item.value === '') {
                      fields.push({id: item.id, value: null})
                    } else {
                      fields.push({id: item.id, value: item.value})
                    }
                  }
                }
              })

              this.axios.put(`waitlistsubscriptions/${this.subscription.id}`, {
                fields: fields
              })
                .then(response => {
                  this.$emit('refreshsubscription', this.subscription.id)
                  resolve(response)
                }).catch(error => {
                reject(error)
              })
            } else {
              reject(false)
            }
          }).catch(() => {
          reject(false)
        })
      })
    },

    updateAccessories () {
      return new Promise((resolve, reject) => {
        const accessories = []

        $.map(this.selectedAccessories, function (amount, accessoryId) {
          accessories.push(parseInt(accessoryId))
        })

        this.axios.put(`waitlistsubscriptions/${this.subscription.id}`, {
          accessories: accessories
        })
          .then(response => {
            this.$emit('refreshsubscription', this.subscription.id)
            resolve(response)
          }).catch(error => {
          reject(error)
        })
      })
    },

    updatePaymentMethod () {
      return new Promise((resolve, reject) => {
        this.axios.put(`waitlistsubscriptions/${this.subscription.id}`, {
          payment_method: this.selectedPaymentMethod
        }).then(response => {
          this.$emit('refreshsubscription', this.subscription.id)
          resolve(response)
        }).catch(error => {
          reject(error)
        })
      })
    },

    switchTab (nextTab) {
      // Validate fields and other things maybe?
      if (this.tabs[this.currentTab].name === 'details') {
        this.saveDetails(this.formScope)
          .then(() => {
            this.currentTab = nextTab
          }).catch(() => {
          this.currentTab = nextTab
        })
      } else if (this.tabs[this.currentTab].name === 'accessories') {
        this.updateAccessories()
          .then(() => {
            this.currentTab = nextTab
          }).catch()
      } else if (this.tabs[this.currentTab].name === 'payment') {
        if (this.tabExists('payment_method') && this.selectedPaymentType) {
          if (this.isDefaultPaymentMethod) {
            if (this.selectedPaymentMethod === 'vipps') {
              let currentDate = new Date()
              const numberOfDaysToAdd = 2
              let result = currentDate.setDate(currentDate.getDate() + numberOfDaysToAdd)
              let vippsMinimumTransactionDate = new Date(result).toISOString().split('T')[0]

              if (this.selectedPaymentType === 'recurring') {
                if (!(this.subscription.transaction_date <= this.currentDate || this.subscription.transaction_date > vippsMinimumTransactionDate)) {
                  this.vippsPaymentAllowed = false
                }
              } else {
                this.vippsPaymentAllowed = true
              }
            }
          } else {
            const _this = this
            this.filteredPaymentMethods = this.paymentMethods.filter(function (currentValue, index) {
              if (currentValue.slug === 'vipps' && _this.$store.getters.activeMerchant.vipps) {
                let currentDate = new Date()
                const numberOfDaysToAdd = 2
                let result = currentDate.setDate(currentDate.getDate() + numberOfDaysToAdd)
                let vippsMinimumTransactionDate = new Date(result).toISOString().split('T')[0]

                if (_this.selectedPaymentType === 'recurring' && (_this.subscription.transaction_date <= _this.currentDate || _this.subscription.transaction_date > vippsMinimumTransactionDate)) {
                  return currentValue
                } else if (_this.selectedPaymentType === 'instant') {
                  return currentValue
                }
              } else if (currentValue.slug === 'nets') {
                return currentValue
              }
            })
          }
        }

        this.currentTab = nextTab
      } else if (this.tabs[this.currentTab].name === 'payment_method') {
        if (nextTab < this.currentTab) {
          // Back button clicked
          if (!this.isDefaultPaymentMethod) {
            this.selectedPaymentMethod = null
          }
        }
        this.currentTab = nextTab
      } else {
        this.currentTab = nextTab
      }
    },

    nextTab () {
      const nextTab = this.currentTab + 1
      if (size(this.tabs[nextTab]) > 0) {
        this.switchTab(nextTab)
      }
    },

    prevTab () {
      const prevTab = this.currentTab - 1
      if (size(this.tabs[prevTab]) > 0) {
        this.switchTab(prevTab)
      }
    },

    tabExists (tabName) {
      return !!find(this.tabs, {name: tabName})
    },

    submitModal () {
      // validation and activation
      if (this.tabExists('details') && this.tabExists('payment') && this.tabExists('accessories')) {
        this.saveDetails(this.formScope)
          .then(() => {
            // refactor
            // Check if a paymentplan has been chosen
            if (this.selectedPaymentplan === null) {
              this.currentTab = findIndex(this.tabs, {name: 'payment'})
            } else {
              // show subscribe button loading
              this.buttonLoader = true
              this.updateAccessories()
                .then(() => {
                  this.axios.put(`waitlistsubscriptions/${this.subscription.id}`, {
                    option_id: this.selectedPaymentplan
                  })
                    .then(() => {
                      // Create a method for this.. Activating a subscription.
                      this.activateSubscription(this.subscription.id)
                        .then(response => {
                          $(this.$el).modal('toggle')
                          this.$nextTick(() => {
                            setTimeout(() => {
                              this.buttonLoader = false
                            }, 550)
                          })
                        }).catch(() => {
                        this.$nextTick(() => {
                          setTimeout(() => {
                            this.buttonLoader = false
                          }, 550)
                        })
                      })
                    }).catch()
                }).catch()
            }
          }).catch(() => {
          // Goto details to fix errors
          this.currentTab = findIndex(this.tabs, {name: 'details'})
        })
      } else if (!this.tabExists('details') && this.tabExists('payment') && this.tabExists('accessories')) {
        if (this.selectedPaymentplan === null) {
          this.currentTab = findIndex(this.tabs, {name: 'payment'})
        } else {
          // show subscribe button loading
          this.buttonLoader = true
          this.updateAccessories()
            .then(() => {
              // attach payment plan
              this.axios.put(`waitlistsubscriptions/${this.subscription.id}`, {
                option_id: this.selectedPaymentplan
              })
                .then(() => {
                  this.activateSubscription(this.subscription.id)
                    .then(response => {
                      this.$nextTick(() => {
                        setTimeout(() => {
                          this.buttonLoader = false
                        }, 550)
                      })
                      $(this.$el).modal('toggle')
                    }).catch(() => {
                    this.$nextTick(() => {
                      setTimeout(() => {
                        this.buttonLoader = false
                      }, 550)
                    })
                  })
                }).catch()
            })
        }
      } else if (!this.tabExists('details') && !this.tabExists('payment') && this.tabExists('accessories')) {
        this.buttonLoader = true
        this.updateAccessories()
          .then(() => {
            this.activateSubscription(this.subscription.id)
              .then(response => {
                this.$nextTick(() => {
                  setTimeout(() => {
                    this.buttonLoader = false
                  }, 550)
                })
                $(this.$el).modal('toggle')
              }).catch(() => {
              this.$nextTick(() => {
                setTimeout(() => {
                  this.buttonLoader = false
                }, 550)
              })
            })
          }).catch()
      } else if (this.tabExists('details') && this.tabExists('payment') && !this.tabExists('accessories')) {
        this.saveDetails(this.formScope)
          .then(() => {
            // refactor
            // Check if a paymentplan has been chosen
            if (this.selectedPaymentplan === null) {
              this.currentTab = findIndex(this.tabs, {name: 'payment'})
            } else {
              // show subscribe button loading
              this.buttonLoader = true
              // attach payment plan
              this.axios.put(`waitlistsubscriptions/${this.subscription.id}`, {
                option_id: this.selectedPaymentplan
              })
                .then(() => {
                  // Create a method for this.. Activating a subscription.
                  this.activateSubscription(this.subscription.id)
                    .then(response => {
                      this.$nextTick(() => {
                        setTimeout(() => {
                          this.buttonLoader = false
                        }, 550)
                      })
                      $(this.$el).modal('toggle')
                    }).catch(() => {
                    this.$nextTick(() => {
                      setTimeout(() => {
                        this.buttonLoader = false
                      }, 550)
                    })
                  })
                }).catch()
            }
          }).catch(() => {
          // Goto details to fix errors
          this.currentTab = findIndex(this.tabs, {name: 'details'})
        })
      } else if (this.tabExists('details') && !this.tabExists('payment') && !this.tabExists('accessories')) {
        this.saveDetails(this.formScope)
          .then(() => {
            // show subscribe button loading
            this.buttonLoader = true
            // activate subscription
            this.activateSubscription(this.subscription.id)
              .then(response => {
                this.$nextTick(() => {
                  setTimeout(() => {
                    this.buttonLoader = false
                  }, 550)
                })
                $(this.$el).modal('toggle')
              }).catch(() => {
              this.$nextTick(() => {
                setTimeout(() => {
                  this.buttonLoader = false
                }, 550)
              })
            })
          }).catch(() => {
          // Goto details to fix errors
          this.currentTab = findIndex(this.tabs, {name: 'details'})
        })
      } else if (!this.tabExists('details') && this.tabExists('payment') && !this.tabExists('accessories')) {
        if (this.selectedPaymentplan === null) {
          this.currentTab = findIndex(this.tabs, {name: 'payment'})
        } else {
          // show subscribe button loading
          this.buttonLoader = true

          this.axios.put(`waitlistsubscriptions/${this.subscription.id}`, {
            option_id: this.selectedPaymentplan
          })
            .then(() => {
              // Create a method for this.. Activating a subscription.
              this.activateSubscription(this.subscription.id)
                .then(response => {
                  this.$nextTick(() => {
                    setTimeout(() => {
                      this.buttonLoader = false
                    }, 550)
                  })
                  $(this.$el).modal('toggle')
                }).catch(() => {
                this.$nextTick(() => {
                  setTimeout(() => {
                    this.buttonLoader = false
                  }, 550)
                })
              })
            }).catch()
        }
      } else if (this.tabExists('details') && !this.tabExists('payment') && this.tabExists('accessories')) {
        this.saveDetails(this.formScope)
          .then(() => {
            // show subscribe button loading
            this.buttonLoader = true
            this.updateAccessories()
              .then(() => {
                this.axios.put(`waitlistsubscriptions/${this.subscription.id}`, {
                  option_id: this.selectedPaymentplan
                })
                  .then(() => {
                    // Create a method for this.. Activating a subscription.
                    this.activateSubscription(this.subscription.id)
                      .then(response => {
                        this.$nextTick(() => {
                          setTimeout(() => {
                            this.buttonLoader = false
                          }, 550)
                        })
                        $(this.$el).modal('toggle')
                      }).catch(() => {
                      this.$nextTick(() => {
                        setTimeout(() => {
                          this.buttonLoader = false
                        }, 550)
                      })
                    })
                  }).catch()
              }).catch()
          }).catch(() => {
          // Goto details to fix errors
          this.currentTab = findIndex(this.tabs, {name: 'details'})
        })
      }
    },

    createVippsMobilePayPayment () {
      this.axios.post(`waitlistsubscriptions/${this.subscription.id}/createPayment`).then((response) => {
        if (response.status === 201) {
          if (response.data.status === 'agreement_exists') {
            this.$events.$emit('alert', {
              header: this.$t('globals.validation.success.success'),
              message: 'Vipps/MobilePay agreement is active. Activating waitlist...',
              type: 'success',
              align: 'right'
            })
          } else if (response.data.status === 'instant_later') {
            this.$events.$emit('alert', {
              header: this.$t('globals.validation.success.success'),
              message: 'Waitlist will be charged later. Activating...',
              type: 'success',
              align: 'right'
            })
          }

          this.activateSubscription(this.subscription.id)
        } else {
          let link = null

          if (response.data.payment_method === 'vipps') {
            link = response.data.url
          }

          $(this.$el).modal('toggle')

          if (link) {
            window.location.href = link
          } else {
            this.$events.$emit('alert', {
              header: this.$t('globals.validation.errors.something_went_wrong'),
              message: this.$t('globals.validation.errors.try_again_later'),
              type: 'error',
              align: 'right'
            })
          }
        }
      }).catch((error) => {
        // $(this.$el).modal('toggle')
        if (error.response) {
          if (error.response.data.message) {
            let messageText

            if (this.isJson(error.response.data.message)) {
              let errorDescription = JSON.parse(error.response.data.message)

              if (errorDescription.hasOwnProperty('error_description')) {
                messageText = errorDescription.error_description.message
              } else if (errorDescription.hasOwnProperty('message')) {
                messageText = errorDescription.message
              } else {
                messageText = this.$t('globals.validation.errors.try_again_later')
              }
            } else {
              messageText = error.response.data.message
            }

            this.$events.$emit('alert', {
              header: this.$t('globals.validation.errors.error'),
              message: messageText,
              type: 'error',
              align: 'right'
            })
          } else {
            this.$events.$emit('alert', {
              header: this.$t('globals.validation.errors.something_went_wrong'),
              message: this.$t('globals.validation.errors.try_again_later'),
              type: 'error',
              align: 'right'
            })
          }
        } else {
          this.$events.$emit('alert', {
            header: this.$t('globals.validation.errors.something_went_wrong'),
            message: this.$t('globals.validation.errors.try_again_later'),
            type: 'error',
            align: 'right'
          })
        }
      })
    },

    validateModal () {
      this.saveDetails(this.formScope)
        .then(() => {
          // Check if a paymentplan has been chosen
          if (this.selectedPaymentplan === null) {
            this.currentTab = findIndex(this.tabs, {name: 'payment'})

            this.$events.$emit('alert', {
              header: this.$t('globals.validation.errors.error'),
              message: this.$t('modals.newsubscriptionmodal.validation.errors.no_interval_selected'),
              type: 'error',
              align: 'right'
            })
          } else if (this.acceptingTerms === true) {
            // show subscribe button loading
            this.buttonLoader = true
            this.updateAccessories()
              .then(() => {
                let data = {
                  option_id: this.selectedPaymentplan,
                  price: this.selectedPaymentPrice
                }

                if (this.selectedPaymentMethod === null) {
                  this.selectedPaymentMethod = 'nets'
                  data['payment_method'] = this.selectedPaymentMethod
                } else {
                  data['payment_method'] = this.selectedPaymentMethod
                }

                this.axios.put(`waitlistsubscriptions/${this.subscription.id}`, data)
                  .then(() => {
                    this.$nextTick(() => {
                      setTimeout(() => {
                        this.buttonLoader = false
                      }, 550)
                    })

                    if (this.selectedPaymentMethod === 'nets') {
                      $(this.$el).modal('toggle')
                      this.$router.push({
                        name: 'checkout',
                        params: {containerId: this.subscription.id},
                        query: {container: 'waitlist'}
                      })
                    } else if (this.selectedPaymentMethod === 'vipps') {
                      this.createVippsMobilePayPayment()
                    }
                  }).catch()
              }).catch()
          } else {
            this.$events.$emit('alert', {
              header: this.$t('globals.validation.errors.error'),
              message: this.$t('modals.newsubscriptionmodal.validation.errors.not_accepting_terms'),
              type: 'error',
              align: 'right'
            })
            this.currentTab = findIndex(this.tabs, {name: 'details'})
          }
        }).catch(() => {
        // Goto details to fix errors
        this.currentTab = findIndex(this.tabs, {name: 'details'})
      })
    },

    cancelModal () {
      // refactor
      if (this.subscription.status === 'deletable') {
        this.axios.delete(`/waitlistsubscriptions/${this.subscription.id}`)
          .then(() => {
            $(this.$el).modal('toggle')
            this.$events.$emit('subscription-cancelled', this.subscription.id)
          }).catch()
      } else {
        $(this.$el).modal('toggle')
        this.$events.$emit('subscription-closed', this.subscription.id)
      }
    },

    loadTabs () {
      return new Promise((resolve, reject) => {
        this.tabs = []
        this.currentTab = 0
        this.tabs.push(
          {
            name: 'product',
            icon: 'cube',
            title: this.$t('modals.newsubscriptionmodal.product.title'),
            description: this.$t('modals.newsubscriptionmodal.product.description')
          },
          {
            name: 'accessories',
            icon: 'shopping basket',
            title: this.$t('modals.newsubscriptionmodal.accessories.title'),
            description: this.$t('modals.newsubscriptionmodal.accessories.description')
          },
          {
            name: 'details',
            icon: 'info',
            title: this.$t('modals.newsubscriptionmodal.details.title'),
            description: this.$t('modals.newsubscriptionmodal.details.description')
          },
          {
            name: 'payment',
            icon: 'payment',
            title: this.$t('modals.newsubscriptionmodal.payment.title'),
            description: this.$t('modals.newsubscriptionmodal.payment.description')
          },
          {
            name: 'payment_method',
            icon: 'money',
            title: this.$t('modals.newsubscriptionmodal.payment_method.title'),
            description: this.$t('modals.newsubscriptionmodal.payment_method.description')
          }
        )
        const _this = this

        var filteredTabs = this.tabs.filter(function (currentValue, index) {
          if (currentValue.name === 'accessories') {
            if (!find(_this.subscription.product.accessories, {public: 1})) {
              if (_this.selectedAccessories) {
                let foundSomething = false
                $.map(_this.selectedAccessories, (index, accessoryId) => {
                  foundSomething = !!find(_this.subscription.product.accessories, {id: parseInt(accessoryId)})
                })

                if (foundSomething) {
                  return currentValue
                }
              }
            } else {
              return currentValue
            }
          } else if (currentValue.name === 'details') {
            // Always return the details since the user should always agree to terms and conditions.
            return currentValue
          } else if (currentValue.name === 'payment') {
            if (_this.subscription.pricing_plans !== null || _this.subscription.pricing_plans.length > 0) {
              return currentValue
            }
          } else if (currentValue.name === 'payment_method') {
            if (_this.$store.getters.activeMerchant.vipps) {
              return currentValue
            }
          } else if (currentValue.name === 'product') {
            return currentValue
          }
        })
        resolve(filteredTabs)
      })
    },

    checkButtons () {
      if (this.currentTab === this.tabs.length - 1) {
        this.showNext = false
      } else {
        this.showNext = true
      }

      if (this.currentTab !== 0) {
        this.showPrevious = true
      } else {
        this.showPrevious = false
      }
    },

    selectPaymentMethod (slug) {
      this.selectedPaymentMethod = slug
    },

    deselectPaymentMethod () {
      this.selectedPaymentMethod = null
    },

    selectAccessory (id) {
      this.selectedAccessories[id] = '1'
    },

    deselectAccessory (id) {
      delete this.selectedAccessories[id]

      if (size(this.selectedAccessories) <= 0 && !find(this.subscription.product.accessories, {public: 1})) {
        this.tabs.splice(findIndex(this.tabs, {name: 'accessories'}), 1)
        this.checkButtons()
      }
    },

    acceptTerms () {
      this.acceptingTerms = true
      $(this.$el).modal('toggle')
    },

    denyTerms () {
      this.acceptingTerms = false
      $(this.$el).modal('toggle')
    },

    selectPaymentplan (id, price, type) {
      // Live code
      this.selectedPaymentplan = id
      this.selectedPaymentPrice = price
      this.selectedPaymentType = type
    },

    refreshPage () {
      this.$router.push({name: 'wait_list'}, () => {
        this.$router.go()
      })
    },

    deselectPaymentplan (id) {
      this.selectedPaymentplan = null
    },

    activateSubscription (subscriptionId) {
      return new Promise((resolve, reject) => {
        this.axios.put(`waitlistsubscriptions/${subscriptionId}/activate`)
          .then((response) => {
            $(this.$el).modal('toggle')

            if (response.data.hasOwnProperty('link')) {
              if (process.env.NODE_ENV === 'development') {
                this.$router.push({
                  name: 'mobile_pay_success',
                  query: {
                    id: subscriptionId,
                    type: 'waitlistsubscriptions',
                    op: response.data.op
                  }
                })
              } else {
                window.location.href = response.data.link.href
              }
            } else {
              this.$events.$emit('updatesubscriptions', response.data)

              if (response.status === 201) {
                this.$events.$emit('alert', {
                  header: this.$t('globals.validation.warning.warning'),
                  message: this.$t('modals.newwaitlistmodal.validation.success.waitlist_added_with_failed_charge'),
                  type: 'warning',
                  align: 'right'
                })
              } else {
                this.$events.$emit('alert', {
                  header: this.$t('globals.validation.success.success'),
                  message: this.$t('modals.newwaitlistmodal.validation.success.waitlist_added'),
                  type: 'success',
                  align: 'right'
                })
              }
            }

            resolve(response.data)
          }).catch(error => {
          if (error.response.data.message) {
            this.$events.$emit('alert', {
              header: this.$t('globals.validation.errors.error'),
              message: error.response.data.message,
              type: 'error',
              align: 'right'
            })
          }
          reject(error)
        })
      })
    },

    isJson (str) {
      try {
        JSON.parse(str)
      } catch (e) {
        return false
      }
      return true
    }
  },

  computed: {
    isMobile () {
      return window.innerWidth < 451
    },
    disableActivateButton () {
      if (this.selectedPaymentplan === null) {
        return true
      }

      if (this.selectedPaymentMethod === 'vipps' && !this.vippsPaymentAllowed) {
        return true
      }

      if (this.filteredPaymentMethods.length > 1 && this.selectedPaymentMethod === null) {
        return true
      }

      return false
    },
    getLanguage () {
      if (this.$root.$i18n.locale) {
        return this.$root.$i18n.locale
      } else {
        if (window.localStorage.language) {
          return window.localStorage.language
        } else if (navigator.language) {
          return navigator.language
        } else {
          return 'da'
        }
      }
    }
  }
}
</script>

<style scoped>
.notice {
  padding-top: 20px;
  color: red !important;
  text-transform: none;
}
.text-danger {
  color: red;
}

@media only screen and (max-width: 768px) {
  .notice {
    padding-bottom: 20px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/waitlist/NewWaitlistModal.vue